/**
 * useFieldLoad (Inventory Screens Step 4.2b) — the field-zone Load hook.
 *
 * Online (FieldAuthContext offline=false): calls get_field_sites through
 * apiFetch (Bearer when acquirable, caller_email fallback rides the query
 * string — the standard fallback-window contract), persists the response into
 * tmg-field via persistLoadResponse (snapshot + site cards), then reads the
 * site count back FROM IDB so what's displayed proves the write path.
 *
 * Offline (offline=true): no network at all — hydrates identity/profile/site
 * count straight from the device snapshot.
 *
 * Online-but-fetch-failed degrades to the cache when one exists (status
 * 'offline' + error retained) — a flaky-signal field device keeps working.
 *
 * TODO(4.3/Step 5): per-site get_field_records fan-out (continuation-token
 * paging, dirty-preserving re-Load reconciliation) rides here. This step
 * persists sites + the identity/profile snapshot — enough to prove Walls 2/3.
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '../../../auth/apiFetch';
import { useUser } from '../../../shared/context/UserContext';
import { useFieldAuth } from '../auth/FieldProtectedRoute';
import { persistLoadResponse, getFieldSnapshot, getSites } from '../db/fieldStore';
import { hydrateFieldRefData } from './fieldRefData';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * @returns {{ status: 'loading'|'online'|'offline'|'error',
 *             identity: Object|null, profile: Object|null,
 *             siteCount: number, error: string|null }}
 */
export function useFieldLoad() {
  const { offline, snapshot: gateSnapshot } = useFieldAuth();
  const { callerEmail, userProfile, isLoading: profileLoading } = useUser();

  const [state, setState] = useState({
    status: 'loading', identity: null, profile: null, siteCount: 0, error: null,
  });

  useEffect(() => {
    let alive = true;
    const set = (next) => { if (alive) setState(next); };

    async function hydrateFromCache(status, error = null) {
      const snap = gateSnapshot || (await getFieldSnapshot());
      if (!snap) return false;
      const sites = await getSites();
      set({
        status,
        identity: snap.identity || null,
        profile: snap.profile || null,
        siteCount: sites.length,
        error,
      });
      return true;
    }

    async function run() {
      // Offline gate said "device snapshot" — never touch the network.
      if (offline) {
        try {
          if (!(await hydrateFromCache('offline'))) {
            set({ status: 'error', identity: null, profile: null, siteCount: 0, error: 'No device snapshot' });
          }
        } catch (err) {
          console.error('useFieldLoad: offline hydrate failed', err);
          set({ status: 'error', identity: null, profile: null, siteCount: 0, error: err.message });
        }
        return;
      }

      // Online: wait for the profile fetch to settle so the snapshot persists
      // a real profile, not a mid-flight null.
      if (profileLoading) return;

      try {
        const params = new URLSearchParams();
        if (callerEmail) params.append('caller_email', callerEmail);
        const res = await apiFetch(`${API_BASE_URL}/get_field_sites?${params}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || `get_field_sites failed: ${res.status}`);
        }
        const sitesResp = await res.json();

        // TODO(4.3/Step 5): get_field_records fan-out lands here.
        await persistLoadResponse({
          sitesResp,
          recordsResp: [],
          identity: sitesResp.identity,
          profile: userProfile,
        });

        // Refdata hydration rides the Load but is deliberately NOT awaited and
        // can NEVER reject into the catch below — a refdata failure must not
        // report a good sites Load as offline.
        hydrateFieldRefData().catch(() => {});

        const sites = await getSites(); // read-back from IDB, not the response
        set({
          status: 'online',
          identity: sitesResp.identity || null,
          profile: userProfile,
          siteCount: sites.length,
          error: null,
        });
      } catch (err) {
        console.error('useFieldLoad: online Load failed', err);
        try {
          if (await hydrateFromCache('offline', err.message)) return;
        } catch (cacheErr) {
          console.error('useFieldLoad: cache fallback failed', cacheErr);
        }
        set({ status: 'error', identity: null, profile: null, siteCount: 0, error: err.message });
      }
    }

    run();
    return () => { alive = false; };
  }, [offline, gateSnapshot, callerEmail, userProfile, profileLoading]);

  return state;
}
